import { sectionViewViewVector } from "building-generation/generationTools/sectionViewClippingPlane";
import type { Doc } from "building-types/doc";
import type { ViewportViewType } from "building-types/views/viewportTypes";
import type { Vec3 } from "geom/vec3";
import { vec3Add, vec3Copy } from "geom/vec3";
import { eyePosFromElevationViewVector } from "lib/house/render/eyePosFromElevationViewVector";
import { elevationReferenceFrameToViewDirection } from "lib/studio/viewPlaneTools";

export function viewportEyePosAndUpVec(
  viewType: ViewportViewType,
  contentSourceId: string,
  doc: Doc,
  mirror: boolean,
  mirrorTranslationX: number,
  targetMaxZ?: number, // In view space coordinates
): { eyePos: Vec3; target: Vec3; viewDirection: Vec3; upVector: Vec3 } | undefined {
  if (viewType === "PLAN_VIEW") {
    return { eyePos: [0, 0, 1000], target: [0, 0, 0], viewDirection: [0, 0, -1], upVector: [0, 1, 0] };
  }

  const upVector: Vec3 = [0, 0, 1];

  if (viewType === "SECTION_VIEW") {
    const sectionView = doc.sectionViews[contentSourceId];
    if (!sectionView) {
      const viewDirection = elevationReferenceFrameToViewDirection.FRONT;
      const eyePos = vec3Copy(eyePosFromElevationViewVector(viewDirection[0], viewDirection[1], viewDirection[2]));
      return { eyePos, target: [0, 0, 0], viewDirection, upVector };
    }

    const viewDirection = vec3Copy(sectionViewViewVector(sectionView, mirror, mirrorTranslationX));
    const eyePos = vec3Copy(eyePosFromElevationViewVector(...viewDirection));
    return { eyePos, target: [0, 0, 0], viewDirection, upVector };
  }

  if (viewType === "ELEVATION_VIEW") {
    const elevationView = doc.elevationViews[contentSourceId];
    const viewDirection = elevationReferenceFrameToViewDirection[elevationView?.referenceFrame ?? "FRONT"];
    const targetDistance = targetMaxZ ? -targetMaxZ : 0;
    const target: Vec3 = [
      viewDirection[0] * targetDistance,
      viewDirection[1] * targetDistance,
      viewDirection[2] * targetDistance,
    ];
    // If we know the maxZ value, we can fit the camera near the target, which will avoid excessive fog in realistic styled views. Otherwise we'll use a far away camera.
    const eyeDistanceFromTarget = targetMaxZ ? 200 : 5000;
    const eyePos = vec3Copy(
      vec3Add(
        ...target,
        ...eyePosFromElevationViewVector(viewDirection[0], viewDirection[1], viewDirection[2], eyeDistanceFromTarget),
      ),
    );
    return { eyePos, target, viewDirection, upVector };
  }
  return undefined;
}
